import { useEffect, useState } from "react";
import { History, RefreshCw, Search, ShieldAlert, Download } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const ACTION_CLS = {
    "mid.create": "bg-emerald-100 text-emerald-800",
    "mid.update": "bg-blue-100 text-blue-800",
    "mid.delete": "bg-rose-100 text-rose-800",
    "wallet.set_vendors": "bg-indigo-100 text-indigo-800",
    "wallet.set_mids": "bg-indigo-100 text-indigo-800",
    "wallet.topup": "bg-amber-100 text-amber-800",
    "admin.create": "bg-emerald-100 text-emerald-800",
    "admin.update": "bg-blue-100 text-blue-800",
    "admin.role_change": "bg-purple-100 text-purple-800",
    "admin.password_reset": "bg-amber-100 text-amber-800",
    "admin.delete": "bg-rose-100 text-rose-800",
};

export default function AuditLogPage() {
    const { user } = useAuth();
    const isSuper = user?.role === "super_admin";
    const [items, setItems] = useState([]);
    const [err, setErr] = useState("");
    const [resource, setResource] = useState("");
    const [action, setAction] = useState("");
    const [partnerId, setPartnerId] = useState("");

    const refresh = async () => {
        setErr("");
        try {
            const params = {};
            if (resource) params.resource = resource;
            if (action) params.action = action;
            if (partnerId) params.partner_id = partnerId.trim();
            const r = await api.get("/admin/audit-log", { params });
            setItems(r.data.items || []);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };

    const downloadCsv = async () => {
        setErr("");
        try {
            const params = { limit: 10000 };
            if (resource) params.resource = resource;
            if (action) params.action = action;
            if (partnerId) params.partner_id = partnerId.trim();
            const r = await api.get("/admin/audit-log.csv", { params, responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `audit-log-${new Date().toISOString().slice(0,10)}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
    };
    useEffect(() => { if (isSuper) refresh(); }, [isSuper]);

    if (!isSuper) {
        return (
            <div data-testid="audit-log-page" className="p-10 max-w-md mx-auto text-center">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                    <ShieldAlert className="w-10 h-10 mx-auto text-amber-600 mb-3" />
                    <h2 className="font-bold text-base mb-1">Super admin only</h2>
                    <p className="text-xs text-slate-600">Audit log access is restricted to super admins.</p>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="audit-log-page" className="space-y-5 p-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <History className="w-6 h-6 text-slate-700" /> Audit Log
                    </h1>
                    <p className="text-xs text-slate-500 mt-1">Immutable record of every sensitive admin action. Rows cannot be edited or deleted.</p>
                </div>
                <button onClick={refresh} data-testid="audit-refresh"
                        className="btn-secondary px-3 py-2 rounded-md text-xs flex items-center gap-1.5">
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
                <button onClick={downloadCsv} data-testid="audit-export-csv"
                        className="btn-primary px-3 py-2 rounded-md text-xs flex items-center gap-1.5 ml-2">
                    <Download className="w-3.5 h-3.5" /> Export CSV
                </button>
            </div>

            <div className="grid grid-cols-4 gap-2">
                <select data-testid="audit-filter-resource" value={resource} onChange={(e) => setResource(e.target.value)} className="input text-xs">
                    <option value="">All resources</option>
                    <option value="mid">MIDs</option>
                    <option value="wallet">Wallets</option>
                    <option value="admin_user">Admin users</option>
                </select>
                <select data-testid="audit-filter-action" value={action} onChange={(e) => setAction(e.target.value)} className="input text-xs">
                    <option value="">All actions</option>
                    <option value="mid.create">mid.create</option>
                    <option value="mid.update">mid.update</option>
                    <option value="mid.delete">mid.delete</option>
                    <option value="wallet.set_vendors">wallet.set_vendors</option>
                    <option value="wallet.set_mids">wallet.set_mids</option>
                    <option value="wallet.topup">wallet.topup</option>
                    <option value="admin.create">admin.create</option>
                    <option value="admin.role_change">admin.role_change</option>
                    <option value="admin.password_reset">admin.password_reset</option>
                    <option value="admin.delete">admin.delete</option>
                </select>
                <input data-testid="audit-filter-partner" value={partnerId} onChange={(e) => setPartnerId(e.target.value)}
                        placeholder="partner_id" className="input text-xs font-mono" />
                <button onClick={refresh} data-testid="audit-apply"
                        className="btn-primary px-3 py-2 rounded-md text-xs flex items-center justify-center gap-1.5">
                    <Search className="w-3.5 h-3.5" /> Apply
                </button>
            </div>

            {err && <div data-testid="audit-error" className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700">{err}</div>}

            <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wider">
                        <tr>
                            <th className="px-3 py-2 text-left">When</th>
                            <th className="px-3 py-2 text-left">Actor</th>
                            <th className="px-3 py-2 text-left">Action</th>
                            <th className="px-3 py-2 text-left">Resource</th>
                            <th className="px-3 py-2 text-left">Details</th>
                            <th className="px-3 py-2 text-left">IP</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No audit entries match these filters.</td></tr>}
                        {items.map((it) => (
                            <tr key={it.audit_id} data-testid={`audit-row-${it.audit_id}`}
                                className="border-t border-slate-100 align-top">
                                <td className="px-3 py-2 whitespace-nowrap text-slate-500 text-[11px]">
                                    {new Date(it.created_at).toLocaleString()}
                                </td>
                                <td className="px-3 py-2">
                                    <div className="font-mono text-[11px]">{it.actor_email}</div>
                                    <div className="text-[10px] text-slate-400">{it.actor_role}</div>
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${ACTION_CLS[it.action] || "bg-slate-100 text-slate-700"}`}>
                                        {it.action}
                                    </span>
                                </td>
                                <td className="px-3 py-2 font-mono text-[11px]">
                                    <div>{it.resource}</div>
                                    <div className="text-[10px] text-slate-400">{it.resource_id || "—"}</div>
                                </td>
                                <td className="px-3 py-2 max-w-[320px]">
                                    <pre className="text-[10px] font-mono bg-slate-50 border border-slate-100 rounded p-1.5 overflow-x-auto whitespace-pre-wrap break-all">
                                        {JSON.stringify(it.details, null, 2)}
                                    </pre>
                                </td>
                                <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{it.ip}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
